import type { PropsWithChildren } from "react"

import { CloseIcon } from "../../../assets/icons"
import cx from "classnames"
import { useCallback, useEffect, useRef } from "react"

import styles from "./index.module.css"

/**
 * Props for the BottomSheet component.
 */
export type BottomSheetProps = PropsWithChildren & {
  /**
   * Controls visibility of the bottom sheet.
   */
  isOpen: boolean

  /**
   * Callback when the bottom sheet is closed.
   */
  onClose: () => void

  /**
   * Title displayed in the header.
   */
  title?: string
}

const SWIPE_THRESHOLD = 100

/**
 * A bottom sheet component that slides up from the bottom of the screen.
 * Supports swipe-to-dismiss gesture, backdrop click, and ESC key.
 * @param {BottomSheetProps} props - The props for the BottomSheet component.
 * @returns {React.JSX.Element} - A React JSX element representing the bottom sheet.
 */
export const BottomSheet = ({ isOpen, onClose, title, children }: BottomSheetProps): React.JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef(0)
  const isDraggingRef = useRef(false)
  const canDragRef = useRef(false)

  const isScrolledToTop = useCallback(() => {
    const content = contentRef.current
    if (!content) return true
    return content.scrollTop <= 0
  }, [])

  const updateTransform = useCallback((deltaY: number) => {
    const content = contentRef.current
    if (!content) return
    const translateY = Math.max(0, deltaY)
    content.style.transform = `translateY(${translateY}px)`
    content.style.transition = "none"
  }, [])

  const resetTransform = useCallback(() => {
    const content = contentRef.current
    if (!content) return
    content.style.transform = ""
    content.style.transition = ""
  }, [])

  const animateClose = useCallback(() => {
    const content = contentRef.current
    if (!content) {
      onClose()
      return
    }
    content.style.transition = "transform 0.2s ease-out, opacity 0.2s ease-out"
    content.style.transform = "translateY(100%)"
    content.style.opacity = "0"
    setTimeout(() => {
      onClose()
    }, 200)
  }, [onClose])

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      startYRef.current = e.touches[0].clientY
      isDraggingRef.current = false
      canDragRef.current = isScrolledToTop()
    },
    [isScrolledToTop],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!canDragRef.current) return

      const touchY = e.touches[0].clientY
      const deltaY = touchY - startYRef.current

      if (deltaY > 0 && isScrolledToTop()) {
        isDraggingRef.current = true
        updateTransform(deltaY)
        e.preventDefault()
      }
    },
    [isScrolledToTop, updateTransform],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isDraggingRef.current) {
        resetTransform()
        return
      }

      const deltaY = e.changedTouches[0].clientY - startYRef.current

      if (deltaY > SWIPE_THRESHOLD) {
        animateClose()
      } else {
        resetTransform()
      }

      isDraggingRef.current = false
    },
    [animateClose, resetTransform],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest("[data-bottom-sheet-handle]")) return

      startYRef.current = e.clientY
      isDraggingRef.current = true

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaY = moveEvent.clientY - startYRef.current
        if (deltaY > 0) {
          updateTransform(deltaY)
        }
      }

      const handleMouseUp = (upEvent: MouseEvent) => {
        const deltaY = upEvent.clientY - startYRef.current

        if (deltaY > SWIPE_THRESHOLD) {
          animateClose()
        } else {
          resetTransform()
        }

        isDraggingRef.current = false
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    },
    [animateClose, updateTransform, resetTransform],
  )

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.style.transform = ""
      contentRef.current.style.transition = ""
      contentRef.current.style.opacity = ""
    }
  }, [isOpen])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <>
      <div
        className={cx(styles.backdrop, {
          [styles.backdropVisible]: isOpen,
        })}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div
        className={cx(styles.container, {
          [styles.containerVisible]: isOpen,
        })}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "bottom-sheet-title" : undefined}
      >
        <div
          ref={contentRef}
          className={styles.content}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div className={styles.handleContainer} data-bottom-sheet-handle>
            <div className={styles.handle} />
          </div>

          {title && (
            <header className={styles.header}>
              <h2 id="bottom-sheet-title" className={styles.title}>
                {title}
              </h2>
              <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                <CloseIcon />
              </button>
            </header>
          )}

          <div className={styles.body}>{children}</div>
        </div>
      </div>
    </>
  )
}
